<template>
  <div class="tw-fixed tw-inset-0 tw-z-40" v-if="componentIsActive">
    <Transition
      name="fade"
      @after-leave="dialogClosed"
      @before-enter="dialogBeforeShow"
      @after-enter="$emit('shown')"
    >
      <div v-if="isShowing">
        <div
          class="modal-bg tw-absolute tw-inset-0 tw-z-modal tw-bg-mainbg1 tw-opacity-75"
        ></div>
        <div
          class="sf-modal tw-absolute tw-inset-0 tw-z-modal tw-text-center tw-h-full"
          :class="modalSize"
          @mousedown="bgWillHide = closeOnBg"
          @mouseup="onBgClose"
        >
          <div class="sf-dialog-container">
            <div class="tw-h-full">
              <div
                class="sf-dialog"
                :class="footerBehavior"
                @mousedown.stop
                @mouseup.stop="bgWillHide = false"
              >
                <div class="tw-px-4 md:tw-px-12">
                  <div class="modal-title">
                    <slot name="title" />
                  </div>
                  <div
                    v-if="canClose"
                    class="tw-absolute tw-top-1 tw-right-0 tw-text-lg tw-py-2 tw-px-4"
                  >
                    <div class="mdtall:tw-hidden">
                      <CloseX size="28px" @click="onClose" />
                    </div>
                    <div class="tw-hidden mdtall:tw-block">
                      <CloseX size="38px" @click="onClose" />
                    </div>
                  </div>
                </div>
                <div
                  ref="scrollable"
                  class="slotp tw-px-6 md:tw-px-12"
                >
                  <slot :scroll="scrollContent" />
                  <div v-if="isSimple" class="sf-modal-footer">
                    <button
                      v-if="simpleOkLabel"
                      type="button"
                      @click.prevent="$emit('ok')"
                      class="sf-btn-primary"
                    >
                      {{ simpleOkLabel }}
                    </button>
                    <button
                      v-if="simpleCancelLabel"
                      type="button"
                      @click="$emit('cancel')"
                      class="sf-btn-secondary"
                    >
                      {{ simpleCancelLabel }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="postcss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-active .sf-modal,
.fade-leave-active .sf-modal {
  transition: all 0.5s ease;
}
.fade-enter-from .sf-modal,
.fade-leave-to .sf-modal {
  top: -80px;
}

.modal-title {
  @apply tw-text-left tw-mb-2 tw-text-xl tw-text-primary tw-font-bold tw-pr-[40px];
  @apply lgtall:tw-text-2xl xltall:tw-text-3xl mdtall:tw-mb-8;
}
</style>

<script lang="ts">
// Change bg1 color in template to the name of the color to use for the borders
import { defineComponent, PropType } from "vue";
import { SfSizeOption } from "@/types/generalTypes";
import CloseX from "./CloseX.vue";
import { preventScroll } from "@/state/appState";

export default defineComponent({
  components: { CloseX },
  props: {
    size: String as PropType<SfSizeOption>,
    show: Boolean,
    isConfirm: Boolean,
    isSimple: Boolean,
    simpleOkLabel: String,
    simpleCancelLabel: String,
    canClose: { type: Boolean, default: true },
    preventBodyScroll: { type: Boolean, default: false },
    footerBehavior: { 
      type: String as PropType<'sticky' | 'normal'>, 
      default: 'normal' 
    },
    closeOnBg: { type: Boolean, default: false },
    beforeClose: Function as PropType<() => boolean>,
  },
  data() {
    return {
      isShowing: false,
      componentIsActive: false,
      bgWillHide: false,
    };
  },
  emits: ["beforeShow", "shown", "closed", "ok", "cancel"],
  computed: {
    hasFooter(): boolean {
      return !!this.$slots.footer;
    },
    modalSize(): SfSizeOption {
      return this.size ? this.size : "md";
    },
    scrollable(): HTMLElement {
      return this.$refs.scrollable as HTMLElement;
    },
  },
  mounted() {
    if (this.show) {
      this.componentIsActive = true;
      this.$nextTick(() => {
        this.isShowing = true;
      });
    }
  },
  beforeUnmount() {
    preventScroll(false);
  },
  methods: {
    dialogBeforeShow() {
      this.$emit("beforeShow");
      if (this.preventBodyScroll) {
        preventScroll(true);
      }
    },
    dialogClosed() {
      this.componentIsActive = false;
      this.$emit("closed");
      if (this.preventBodyScroll) {
        preventScroll(false);
      }
    },
    scrollContent(tgt: number) {
      this.$nextTick(() => {
        if (tgt == -1) {
          tgt = this.scrollable.scrollHeight;
        }
        this.scrollable.scroll({ top: tgt });
      });
    },
    onBgClose() {
      if (!this.canClose) {
        return;
      }
      if (this.bgWillHide) {
        this.onClose();
      }
      this.bgWillHide = false;
    },
    async onClose() {
      if (this.beforeClose) {
        const shouldClose = await this.beforeClose();
        if (!shouldClose) {
          return;
        }
      }
      this.isShowing = false;
    },
  },
  watch: {
    show() {
      if (this.show) {
        this.componentIsActive = true;
      }
      this.$nextTick(() => {
        this.isShowing = this.show;
      });
    },
  },
});
</script>
